import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTodayHK } from "../lib/date";

type Metal = "gold" | "platinum" | "grain" | "gold_gram";

interface AdminFormRow {
  metal: Metal;
  label: string;
  subLabel?: string;
}

const adminRows: AdminFormRow[] = [
  { metal: "gold", label: "千足金", subLabel: "999.9" },
  { metal: "gold_gram", label: "千足金(克)", subLabel: "999.9" },
  { metal: "platinum", label: "鉑金", subLabel: "999.5" },
  { metal: "grain", label: "金粒" },
];

interface PriceInputState {
  sell_hkd: string;
  sell_mop: string;
  sell_cny: string;
  buy_hkd: string;
  buy_mop: string;
  buy_cny: string;
}

type FormState = Record<Metal, PriceInputState>;

const emptyState: PriceInputState = {
  sell_hkd: "",
  sell_mop: "",
  sell_cny: "",
  buy_hkd: "",
  buy_mop: "",
  buy_cny: "",
};

function createInitialFormState(): FormState {
  return {
    gold: { ...emptyState },
    gold_gram: { ...emptyState },
    platinum: { ...emptyState },
    grain: { ...emptyState },
  };
}

const fetchOpts: RequestInit = { credentials: "include" };

export function AdminEntryPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const editDate = params.get("date");
  const editBatch = params.get("created_at");
  const isEditMode = !!(editDate && editBatch);

  const [form, setForm] = useState<FormState>(() => createInitialFormState());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (isEditMode) {
          const res = await fetch(
            `/api/admin/prices?date=${encodeURIComponent(editDate)}&created_at=${encodeURIComponent(editBatch)}`,
            { ...fetchOpts, cache: "no-store" }
          );
          if (res.status === 401) {
            navigate("/admin", { replace: true });
            return;
          }
          if (!res.ok) throw new Error("讀取失敗");
          const json = (await res.json()) as {
            items: Array<{
              metal: Metal;
              sell_hkd: number;
              sell_mop: number;
              sell_cny?: number | null;
              buy_hkd: number;
              buy_mop: number;
              buy_cny?: number | null;
            }>;
          };
          if (cancelled) return;
          const next = createInitialFormState();
          for (const item of json.items ?? []) {
            if (item.metal in next) {
              next[item.metal] = {
                sell_hkd: String(item.sell_hkd ?? ""),
                sell_mop: String(item.sell_mop ?? ""),
                sell_cny: String(item.sell_cny ?? ""),
                buy_hkd: String(item.buy_hkd ?? ""),
                buy_mop: String(item.buy_mop ?? ""),
                buy_cny: String(item.buy_cny ?? ""),
              };
            }
          }
          setForm(next);
        } else {
          const res = await fetch("/api/prices/today", { cache: "no-store" });
          if (!res.ok) return;
          const json = (await res.json()) as {
            items?: Array<{
              metal: Metal;
              sell_hkd: number;
              sell_mop: number;
              sell_cny?: number | null;
              buy_hkd: number;
              buy_mop: number;
              buy_cny?: number | null;
            }>;
          };
          if (cancelled) return;
          const next = createInitialFormState();
          for (const item of json.items ?? []) {
            if (item.metal in next) {
              next[item.metal] = {
                sell_hkd: String(item.sell_hkd ?? ""),
                sell_mop: String(item.sell_mop ?? ""),
                sell_cny: String(item.sell_cny ?? ""),
                buy_hkd: String(item.buy_hkd ?? ""),
                buy_mop: String(item.buy_mop ?? ""),
                buy_cny: String(item.buy_cny ?? ""),
              };
            }
          }
          setForm(next);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editDate, editBatch, isEditMode, navigate]);

  const handleChange = (
    metal: Metal,
    field: keyof PriceInputState,
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      [metal]: { ...prev[metal], [field]: value },
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const items = adminRows.map((row) => {
        const s = form[row.metal];
        return {
          metal: row.metal,
          sell_hkd: Number(s.sell_hkd || 0),
          sell_mop: Number(s.sell_mop || 0),
          sell_cny:
            row.metal === "gold_gram" && s.sell_cny.trim() !== ""
              ? Number(s.sell_cny)
              : null,
          buy_hkd: Number(s.buy_hkd || 0),
          buy_mop: Number(s.buy_mop || 0),
          buy_cny:
            row.metal === "gold_gram" && s.buy_cny.trim() !== ""
              ? Number(s.buy_cny)
              : null,
        };
      });

      const date = editDate ?? getTodayHK();

      const res = await fetch("/api/admin/prices", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify(
          isEditMode
            ? { items, date, overwrite: true, created_at: editBatch }
            : { items, date }
        ),
      });

      if (res.status === 401) {
        navigate("/admin", { replace: true });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "儲存失敗");
      }

      setMessage(
        isEditMode
          ? `已成功儲存（覆蓋）${date} ${editBatch}`
          : `已成功新增 ${date} 最新一筆`
      );
      setTimeout(() => navigate("/admin"), 800);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "儲存失敗");
    } finally {
      setSubmitting(false);
    }
  };

  const displayDate = editDate ?? getTodayHK();

  return (
    <div className="page page-admin">
      <div className="entry-header">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate("/admin")}
        >
          ← 返回
        </button>
        <h1 className="page-title" style={{ margin: 0 }}>
          {isEditMode ? "編輯報價" : "新增報價"}
        </h1>
      </div>

      <div className="card">
        <div className="entry-meta">
          <span>日期：{displayDate}</span>
          {isEditMode && <span>批次：{editBatch}</span>}
          {!isEditMode && (
            <span className="entry-hint">已預填最後一筆報價</span>
          )}
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : (
          <form onSubmit={handleSubmit}>
            {adminRows.map((row) => {
              const value = form[row.metal];
              return (
                <div key={row.metal} className="metal-form-section">
                  <div className="metal-form-title">
                    <span>{row.label}</span>
                    {row.subLabel && (
                      <span className="metal-form-sub">{row.subLabel}</span>
                    )}
                  </div>
                  <div className="metal-form-grid">
                    <div className="metal-form-corner" />
                    <div className="metal-form-colhead">賣出</div>
                    <div className="metal-form-colhead">買入</div>
                    {row.metal !== "gold_gram" && (
                      <>
                        <div className="metal-form-ccy">HKD</div>
                        <input
                          inputMode="numeric"
                          value={value.sell_hkd}
                          onChange={(e) =>
                            handleChange(
                              row.metal,
                              "sell_hkd",
                              e.target.value
                            )
                          }
                        />
                        <input
                          inputMode="numeric"
                          value={value.buy_hkd}
                          onChange={(e) =>
                            handleChange(
                              row.metal,
                              "buy_hkd",
                              e.target.value
                            )
                          }
                        />
                      </>
                    )}
                    <div className="metal-form-ccy">MOP</div>
                    <input
                      inputMode="numeric"
                      value={value.sell_mop}
                      onChange={(e) =>
                        handleChange(row.metal, "sell_mop", e.target.value)
                      }
                    />
                    <input
                      inputMode="numeric"
                      value={value.buy_mop}
                      onChange={(e) =>
                        handleChange(row.metal, "buy_mop", e.target.value)
                      }
                    />
                    {row.metal === "gold_gram" && (
                      <>
                        <div className="metal-form-ccy">CNY</div>
                        <input
                          inputMode="numeric"
                          value={value.sell_cny}
                          onChange={(e) =>
                            handleChange(
                              row.metal,
                              "sell_cny",
                              e.target.value
                            )
                          }
                        />
                        <input
                          inputMode="numeric"
                          value={value.buy_cny}
                          onChange={(e) =>
                            handleChange(
                              row.metal,
                              "buy_cny",
                              e.target.value
                            )
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting
                ? "儲存中…"
                : isEditMode
                  ? `儲存並覆蓋`
                  : `新增一筆更新`}
            </button>
            {message && (
              <div style={{ marginTop: 12, fontSize: 14 }}>{message}</div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
